import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import { UploadCloud, X, CheckCircle2 } from "lucide-react";

export default function AdSpendUploadDialog({ open, onOpenChange, onCommitted }) {
  const { current } = useWorkspace();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setResult(null);
  }, [open]);

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    if (list[0]) setFile(list[0]);
  };

  const upload = async () => {
    if (!current || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ad-spend/upload", fd, {
        params: { workspace_id: current.id },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Updated ${data.dates_updated} day(s) with ad spend`);
      onCommitted?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-xl" data-testid="ad-spend-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Upload Ad Spend</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            CSV headers required: <span className="font-mono">date, channel, spend</span>. Channel is bucketed: meta / facebook / instagram → Meta; google / adwords / youtube → Google.
          </p>
        </DialogHeader>

        {!result && (
          <div className="space-y-5">
            <div>
              <Label className="overline">CSV File</Label>
              <label
                htmlFor="ad-spend-file-input"
                className="mt-2 border-2 border-dashed border-border p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-accent transition-colors"
                data-testid="ad-spend-dropzone"
              >
                <UploadCloud className="h-8 w-8 text-muted-foreground mb-2" />
                <div className="text-sm">Click to select an ad spend CSV</div>
                <div className="text-xs text-muted-foreground mt-1">One row per (date, channel, spend). Multiple rows per date/channel are summed.</div>
                <input
                  id="ad-spend-file-input"
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={onFileChange}
                  data-testid="ad-spend-file-input"
                />
              </label>
              {file && (
                <div className="mt-3 flex items-center justify-between border border-border px-3 py-2" data-testid="ad-spend-file-name">
                  <div className="text-sm font-mono truncate">{file.name}</div>
                  <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
              <Button
                onClick={upload}
                disabled={uploading || !file}
                className="rounded-none"
                data-testid="ad-spend-upload-btn"
              >
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-5">
            <div className="border border-border p-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--positive))] shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">{result.rows_parsed} row(s) parsed · {result.dates_updated} day(s) updated</div>
                <div className="mt-1 text-muted-foreground font-mono text-xs">
                  Meta: {inr(result.totals.meta_ad_spend || 0, { decimals: 0 })} · Google: {inr(result.totals.google_ad_spend || 0, { decimals: 0 })}
                </div>
                {result.skipped_bad_date > 0 && (
                  <div className="mt-1 text-xs text-[hsl(var(--negative))]">Skipped {result.skipped_bad_date} row(s) with unparseable dates.</div>
                )}
                {result.skipped_unknown_channel && Object.keys(result.skipped_unknown_channel).length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Skipped unknown channels: {Object.entries(result.skipped_unknown_channel).map(([k, v]) => `${k} (${v})`).join(", ")}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResult(null); setFile(null); }} className="rounded-none">Upload another</Button>
              <Button onClick={() => onOpenChange(false)} className="rounded-none" data-testid="ad-spend-done-btn">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
